/**
 * Lightweight frontend logger with level control.
 *
 * - In development: all levels (debug, info, warn, error) are active.
 * - In production:  only warn and error fire; debug and info are suppressed.
 *
 * Usage:
 *   import logger from '../utils/logger';
 *   logger.info('auth', 'User logged in', { email });
 *   logger.error('api', 'Server error', { url, status });
 */

const LOG_LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

const MIN_LEVEL =
  process.env.NODE_ENV === 'production' ? LOG_LEVELS.warn : LOG_LEVELS.debug;

function shouldLog(level) {
  return LOG_LEVELS[level] >= MIN_LEVEL;
}

function formatEntry(level, category, message, data) {
  return {
    timestamp: new Date().toISOString(),
    level,
    category,
    message,
    ...data,
  };
}

const logger = {
  debug(category, message, data = {}) {
    if (!shouldLog('debug')) return;
    console.debug(`[${category}]`, message, data);
  },

  info(category, message, data = {}) {
    if (!shouldLog('info')) return;
    console.info(`[${category}]`, message, data);
  },

  warn(category, message, data = {}) {
    if (!shouldLog('warn')) return;
    console.warn(`[${category}]`, message, data);
  },

  error(category, message, data = {}) {
    if (!shouldLog('error')) return;
    const entry = formatEntry('error', category, message, data);
    console.error(`[${category}]`, message, data);

    // Future: send to backend or external service (Sentry, etc.)
    // sendToServer(entry);
    void entry;
  },
};

export default logger;
